import React from 'react';
import { CalendarCheck2, FileBadge, Gauge, Mail, MessageSquare, Phone } from 'lucide-react';

import { cn } from '../../../../lib/utils';
import dayjs from '../../../../lib/day';

export const SIDE_CARD =
  'rounded-2xl bg-white p-[18px] shadow-[0_1px_2px_rgba(15,23,42,0.04),0_2px_10px_rgba(15,23,42,0.06)] dark:!bg-slate-900 dark:ring-1 dark:ring-white/10 sm:p-[20px]';
export const EYEBROW = 'text-[11px] font-bold uppercase tracking-wider text-slate-400 dark:text-slate-500';

const formatPhone = (p) => {
  const d = String(p || '').replace(/\D/g, '').slice(-10);
  return d.length === 10 ? `(${d.slice(0, 3)}) ${d.slice(3, 6)}-${d.slice(6)}` : p || '';
};
const initialsOf = (first, last) => `${(first || '?')[0]}${(last || '')[0] || ''}`.toUpperCase();

// Who is helping them, and how to reach them. Tap to call on a phone only
// (Nic 2026-10-05): on a computer a call button confuses people, so the
// number is plain text there.
export const TeamCard = ({ title, person, waiting }) => (
  <section className={SIDE_CARD}>
    <div className={EYEBROW}>{title}</div>
    {person ? (
      <>
        <div className="mt-3 flex items-center gap-3">
          <span className="flex size-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-indigo-600 text-sm font-bold text-white">
            {initialsOf(person.userFirstName, person.userLastName)}
          </span>
          <div className="min-w-0">
            <div className="truncate text-base font-bold text-slate-900 dark:text-white">
              {`${person.userFirstName || ''} ${person.userLastName || ''}`.trim()}
            </div>
            <div className="text-xs text-slate-500 dark:text-slate-400">Here to help with anything</div>
          </div>
        </div>
        <div className="mt-4 grid gap-2">
          {person.internalUserPhone ? (
            <>
              <a
                href={`tel:${person.internalUserPhone}`}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white no-underline hover:bg-slate-700 sm:hidden dark:!bg-white dark:!text-slate-900"
              >
                <Phone size={16} /> Call {formatPhone(person.internalUserPhone)}
              </a>
              <div className="hidden items-center gap-2.5 rounded-xl bg-slate-50 px-3.5 py-2.5 text-sm dark:!bg-white/5 sm:flex">
                <Phone size={15} className="shrink-0 text-slate-400" />
                <span className="select-all font-semibold text-slate-800 dark:text-slate-100">
                  {formatPhone(person.internalUserPhone)}
                </span>
              </div>
            </>
          ) : null}
          {person.userEmail ? (
            <a
              href={`mailto:${person.userEmail}`}
              className="inline-flex h-11 items-center gap-2.5 rounded-xl bg-white px-3.5 text-sm font-semibold text-slate-700 no-underline ring-1 ring-slate-200 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10"
            >
              <Mail size={15} className="shrink-0 text-slate-400" />
              <span className="min-w-0 truncate">{person.userEmail}</span>
            </a>
          ) : null}
        </div>
      </>
    ) : (
      <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">{waiting}</div>
    )}
  </section>
);

// Billing questions (Nic 2026-10-26), beside their billing. Call or text on a
// phone only, like the team card: on a computer the number is plain text.
export const BILLING_EMAIL = 'billing@veritasratings.com';
export const BILLING_PHONE = '6152634307';

export const BillingContact = () => (
  <section className={SIDE_CARD} data-testid="billing-contact">
    <div className={EYEBROW}>Billing questions?</div>
    <p className="mb-0 mt-2 text-sm text-slate-600 dark:text-slate-300">Our billing team is happy to help. Call, text or email us.</p>
    <div className="mt-4 grid gap-2">
      <div className="grid grid-cols-2 gap-2 sm:hidden">
        <a
          href={`tel:+1${BILLING_PHONE}`}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-900 px-4 text-sm font-semibold text-white no-underline hover:bg-slate-700 dark:!bg-white dark:!text-slate-900"
        >
          <Phone size={16} /> Call
        </a>
        <a
          href={`sms:+1${BILLING_PHONE}`}
          className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-white px-4 text-sm font-semibold text-slate-700 no-underline ring-1 ring-slate-200 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10"
        >
          <MessageSquare size={16} /> Text
        </a>
      </div>
      <div className="flex items-center gap-2.5 rounded-xl bg-slate-50 px-3.5 py-2.5 text-sm dark:!bg-white/5">
        <Phone size={15} className="shrink-0 text-slate-400" />
        <span className="select-all font-semibold text-slate-800 dark:text-slate-100">{formatPhone(BILLING_PHONE)}</span>
        <span className="text-xs text-slate-500 dark:text-slate-400">call or text</span>
      </div>
      <a
        href={`mailto:${BILLING_EMAIL}`}
        className="inline-flex h-11 items-center gap-2.5 rounded-xl bg-white px-3.5 text-sm font-semibold text-slate-700 no-underline ring-1 ring-slate-200 hover:bg-slate-50 dark:!bg-white/5 dark:text-slate-200 dark:ring-white/10"
      >
        <Mail size={15} className="shrink-0 text-slate-400" />
        <span className="min-w-0 truncate">{BILLING_EMAIL}</span>
      </a>
    </div>
  </section>
);

const Stat = ({ icon: Icon, label, value }) => (
  <div className="flex items-center gap-3 py-2.5">
    <span className="flex size-8 shrink-0 items-center justify-center rounded-lg bg-slate-100 text-slate-500 dark:!bg-white/10 dark:text-slate-300">
      <Icon size={15} />
    </span>
    <div className="min-w-0 flex-1 text-sm text-slate-500 dark:text-slate-400">{label}</div>
    <div className={cn('text-sm font-semibold text-slate-900 dark:text-white')}>{value}</div>
  </div>
);

const TYPE_LABEL = { initial: 'Initial claim', increase: 'Increase', supplemental: 'Supplemental', appeal: 'Appeal' };

// The few facts about their claim people actually look for.
export const ClaimSnapshot = ({ claim }) => {
  const type = String(claim.claimVaType || '').toLowerCase();
  return (
    <section className={SIDE_CARD}>
      <div className={EYEBROW}>Your claim</div>
      <div className="mt-1 divide-y divide-slate-100 dark:divide-white/[0.06]">
        <Stat icon={Gauge} label="Current VA rating" value={claim.claimCurrentRating != null ? `${claim.claimCurrentRating}%` : '-'} />
        <Stat icon={FileBadge} label="Claim type" value={TYPE_LABEL[type] || (type ? type[0].toUpperCase() + type.slice(1) : '-')} />
        <Stat
          icon={CalendarCheck2}
          label="Intent to File"
          value={claim.claimIntentToFileDate ? dayjs.utc(claim.claimIntentToFileDate).format('MMM D, YYYY') : 'Not yet'}
        />
      </div>
    </section>
  );
};
